import { createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from '../../redux/root-reducer';
import { endpoints } from '../../utils/endpoints';
import { fetchJSON } from '../../utils/fetch';
import { logError } from '../../utils/logging';
import { getUtmCodeParams, UtmCodeParams } from '../../utils/url';
import { ConfirmCardPaymentType } from '../purchase-tickets/stripe/use-stripe-payment';
import { Answer } from '../registration-form/custom-form-factory-types';
import {
  CompleteSeriesOrderPayload,
  SeriesInvitesResponse,
  TicketOrderSummaryPayload,
  TicketOrderSummaryResponse,
} from './series-cart-types';
import { updateErroredEventIds } from './series-reducer';
import { calculateSeriesOrderThunk } from './series-thunk';

const get = endpoints();

export const validateSeriesPromoCode = createAsyncThunk<
  { promoCode: string; valid: boolean },
  { promoCode: string; selectedTicketIds: string[] },
  { state: RootState; rejectValue: { promoCode: string; error: string } }
>(
  'seriesCart/validateSeriesPromoCode',
  async ({ promoCode, selectedTicketIds }, { rejectWithValue }) => {
    try {
      if (!promoCode) {
        return rejectWithValue({
          promoCode: '',
          error: 'series.promo-code-empty',
        });
      }
      const response = await fetchJSON(
        get('VALIDATE_SERIES_PROMO_CODE')(promoCode, selectedTicketIds),
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        },
      );

      if (!response.valid) {
        return { promoCode, valid: false };
      }

      return { promoCode, valid: true };
    } catch (error) {
      return rejectWithValue({ promoCode, error: 'series.promo-code-invalid' });
    }
  },
);

export const applySeriesPromoCode = createAsyncThunk<
  void,
  { eventSlug: string },
  { state: RootState; rejectValue: string }
>(
  'seriesCart/applySeriesPromoCode',
  async ({ eventSlug }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const selectedTicketIds = Object.values(state.series.selectedTicketIds);
      const promoCode = state.seriesCart.promoCode.code;

      const response = await dispatch(
        validateSeriesPromoCode({ promoCode: promoCode, selectedTicketIds }),
      ).unwrap();

      if (response?.valid) {
        // TODO: Remove promoCodeEnabled once the promo code feature flag is always enabled
        await dispatch(
          calculateSeriesOrderThunk({
            eventSlug,
            promoCodeEnabled: true,
            skipPromoCodeValidation: true,
          }),
        );
      }
    } catch (error) {
      return rejectWithValue('series.promo-code-invalid');
    }
    return;
  },
);

export const calculateSeriesOrderSummary = createAsyncThunk<
  TicketOrderSummaryResponse,
  TicketOrderSummaryPayload,
  {
    rejectValue: {
      errors?: string[];
      eventId?: string;
    };
    state: RootState;
  }
>(
  'seriesCart/calculateOrderSummary',
  async (payload, { rejectWithValue, getState, dispatch }) => {
    try {
      // Get selected ticket IDs from series state
      const selectedTicketIds = getState().series.selectedTicketIds;
      const promoCode = getState().seriesCart.promoCode;

      const response = await fetchJSON(
        get('ORDER_SUMMARY_SERIES')(
          payload.eventSlug,
          payload.eventSeriesExternalId,
        ),
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: {
            tickets: payload.tickets.map(ticket => ({
              external_id: ticket.ticketExternalId,
              ...(ticket.orderId && { order_id: ticket.orderId }),
            })),
            email: payload.email || 'registrations@hopin.to',
            first_name: payload.first_name || 'first_name_placeholder',
            last_name: payload.last_name || 'last_name_placeholder',
            event_series_external_id: payload.eventSeriesExternalId || '',
            ...(payload.seriesOrderExternalId && {
              series_order_external_id: payload.seriesOrderExternalId,
            }),
            ...(promoCode.status === 'valid' &&
              promoCode.code.length > 0 && {
                promo_code: promoCode.code,
              }),
          },
        },
      );
      return {
        ...response,
        selectedTicketIds, // Include selected ticket IDs in the response
      };
    } catch (error: any) {
      logError(error);
      const eventId = error.response?.body?.event_external_id;
      if (eventId) {
        dispatch(
          updateErroredEventIds({
            eventId,
            errorMessages: error.response?.body?.errors,
          }),
        );
        return rejectWithValue({
          eventId,
        });
      } else {
        return rejectWithValue({
          errors: error.response?.body?.errors || [
            'Failed to calculate series order summary',
          ],
        });
      }
    }
  },
);

export const seriesCheckout = createAsyncThunk<
  {
    success: boolean;
    redirectToEvent: boolean;
    errorMessage?: string;
    seriesOrderExternalId?: string;
  },
  {
    eventSlug: string;
    email: string;
    confirmCardPayment: ConfirmCardPaymentType;
    acceptedTerms: boolean;
  },
  {
    rejectValue: { error: string };
    state: RootState;
  }
>(
  'series/checkout',
  async (
    { eventSlug, confirmCardPayment, acceptedTerms = false },
    { rejectWithValue, getState, dispatch },
  ) => {
    const seriesPaymentURL = get(`SERIES_PAYMENT_TOKEN`)(eventSlug);
    const state = getState() as RootState;

    const {
      eventDetails: { widgetSource: source, widgetId },
      seriesCart: { seriesOrderExternalId, seriesCartTotal },
      authentication: { token },
      series: {
        selectedTicketIds,
        selectedEvents,
        activeSeries: eventSeriesExternalId,
        questions: {
          userDetails: { email, first_name, last_name },
        },
      },
    } = state;

    let tickets = Object.entries(selectedTicketIds)
      .filter(([_, value]) => !!value)
      .map(([eventId, ticketExternalId]) => ({
        ticketExternalId: selectedEvents[eventId]?.isSelected
          ? ticketExternalId
          : '',
        orderId:
          state.seriesCart.selectedTicketOrderSummary[
            eventId
          ]?.id?.toString() || '',
        eventId,
      }));

    // filter out tickets whose events are not selected
    tickets = tickets.filter(
      ticket => !!selectedEvents[ticket.eventId]?.isSelected,
    );

    try {
      //1. Call series_calulate with actual email
      await dispatch(
        calculateSeriesOrderSummary({
          eventSlug,
          eventSeriesExternalId: eventSeriesExternalId || '',
          tickets,
          email,
          first_name,
          last_name,
          seriesOrderExternalId: seriesOrderExternalId || '',
        }),
      ).unwrap();

      // 1. Get payment token

      let paymentResult = undefined;
      if (seriesCartTotal > 0) {
        const response = await fetchJSON(seriesPaymentURL, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: {
            series_order_external_id: seriesOrderExternalId,
            event_series_external_id: eventSeriesExternalId,
          },
        });

        if (!response || !('client_secret' in response)) {
          return rejectWithValue({ error: 'Invalid payment token response' });
        }

        // 2. Confirm card payment
        paymentResult = await confirmCardPayment(
          email,
          response.client_secret as string,
        );

        if (paymentResult?.error) {
          return rejectWithValue({
            error: paymentResult.error.message || 'Payment failed',
          });
        }
      }
      // 3. Complete order
      const { success } = await completeSeriesOrder({
        eventSlug,
        seriesOrderExternalId: seriesOrderExternalId || '',
        eventSeriesExternalId: eventSeriesExternalId || '',
        stripeId: paymentResult?.paymentIntent?.id || '',
        customerId: email,
        status: paymentResult?.paymentIntent?.status || 'succeeded',
        source,
        widgetId: widgetId || '',
        acceptedTerms,
        token: token || '',
      });

      if (!success) {
        return rejectWithValue({
          error: 'Failed to complete order',
        });
      }
      // 3. Return success result
      return {
        success,
        redirectToEvent: false,
        seriesOrderExternalId,
      };
    } catch (error: any) {
      return rejectWithValue(error);
    }
  },
);

export const formatSeriesFormAnswersPayload = ({
  invitesResponse,
  seriesAnswers,
  selectedTickets,
}: {
  invitesResponse: SeriesInvitesResponse[];
  seriesAnswers: Record<string, Answer[]>;
  selectedTickets: Record<string, string>;
}) => {
  return invitesResponse
    .map(invite => {
      const eventExternalId = Object.keys(selectedTickets).find(
        key => selectedTickets[key] === invite.ticket_external_id,
      );

      if (!eventExternalId) return null;

      const answersForInvite = seriesAnswers[eventExternalId];

      if (!answersForInvite) return null;

      return {
        invite_id: invite.invites[0].id,
        answers: answersForInvite,
      };
    })
    .filter(Boolean);
};

export const submitSeriesFormAnswers = createAsyncThunk<
  void,
  {
    seriesOrderExternalId: string;
    seriesAnswers: Record<string, Answer[]>;
    selectedTickets: Record<string, string>;
  }
>(
  'series/submitFormAnswers',
  async ({ seriesOrderExternalId, seriesAnswers, selectedTickets }) => {
    const invitesResponse = await fetchJSON(
      get('SERIES_ORDER_INVITES')(seriesOrderExternalId),
    );

    if (invitesResponse) {
      const answersWithInviteId = formatSeriesFormAnswersPayload({
        invitesResponse,
        seriesAnswers,
        selectedTickets,
      });

      if (answersWithInviteId.length > 0) {
        await fetchJSON(get('SERIES_FORM_ANSWERS')(seriesOrderExternalId), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: {
            invites_answers: answersWithInviteId,
          },
        });
      }
    }
  },
);

const completeSeriesOrder = async ({
  eventSlug,
  seriesOrderExternalId,
  eventSeriesExternalId,
  stripeId,
  customerId,
  status,
  source,
  widgetId,
  acceptedTerms = false,
  token,
}: CompleteSeriesOrderPayload) => {
  const headers: HeadersInit = {
    Accept: 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const body: Record<
      string,
      string | number | UtmCodeParams | boolean | null
    > = {
      series_order_external_id: seriesOrderExternalId,
      event_series_external_id: eventSeriesExternalId,
      customer_id: customerId,
      status: status,
      widget_id: widgetId,
      source,
      accepted_terms: acceptedTerms,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (stripeId) {
      body.stripe_id = stripeId;
    }
    body.utm_details = getUtmCodeParams(window.location.search);
    const seriesOrderResponse = await fetchJSON(
      get('COMPLETE_SERIES_ORDER')(eventSlug),
      {
        method: 'POST',
        headers: headers,
        body,
      },
    );

    return {
      success: true,
      orderSlug: seriesOrderResponse.slug,
      authRegistered: seriesOrderResponse.auth_registered,
    };
  } catch (err) {
    logError(err as any);
    return {
      success: false,
      orderSlug: '',
      authRegistered: false,
    };
  }
};
